/**
 * Per-classroom temperature display unit. Picked on the Weather
 * settings page; the live Weather page (Thermometer + "right now
 * outside" card + history table) reads from here.
 *
 *   'F'     → numeric °F (default — what we've always shown)
 *   'C'     → numeric °C, converted from the underlying °F value
 *   'words' → "Cold / Cool / Warm / Hot" — for younger grades who
 *             are still learning to associate readings with descriptors
 *
 * Stored in localStorage per classroom until a backend column lands
 * (same approach as enabledStorage / themeStorage / schoolYearStorage).
 */

export type TemperatureUnit = 'F' | 'C' | 'words';

const KEY = 'classy.temperatureUnit.v1';
const DEFAULT_UNIT: TemperatureUnit = 'F';

type Map = Record<string, TemperatureUnit>;

function read(): Map {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Map) : {};
  } catch {
    return {};
  }
}

function write(map: Map): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* swallow — quota / private mode */
  }
}

function isUnit(v: unknown): v is TemperatureUnit {
  return v === 'F' || v === 'C' || v === 'words';
}

export function getTemperatureUnit(classroomId: string): TemperatureUnit {
  const v = read()[classroomId];
  return isUnit(v) ? v : DEFAULT_UNIT;
}

export function setTemperatureUnit(classroomId: string, unit: TemperatureUnit): void {
  const map = read();
  map[classroomId] = unit;
  write(map);
}

/** Convert an underlying Fahrenheit value (the storage format) to the
 *  display value for the given unit. Always rounds to the nearest
 *  integer so the readout doesn't fight the slider's whole-degree steps. */
export function formatTemperature(fahrenheit: number | null, unit: TemperatureUnit): string {
  if (fahrenheit === null) return '—';
  if (unit === 'C') {
    const c = Math.round(((fahrenheit - 32) * 5) / 9);
    return `${c}°C`;
  }
  if (unit === 'words') {
    return temperatureWord(fahrenheit);
  }
  return `${Math.round(fahrenheit)}°F`;
}

/** Kid-friendly bucket the °F value into one of four descriptive
 *  words. Bands tuned for the 0–110 °F slider range:
 *
 *    < 40 °F   →  Cold
 *    40–64 °F  →  Cool
 *    65–84 °F  →  Warm
 *    ≥ 85 °F   →  Hot
 */
export function temperatureWord(fahrenheit: number): string {
  if (fahrenheit < 40) return 'Cold';
  if (fahrenheit < 65) return 'Cool';
  if (fahrenheit < 85) return 'Warm';
  return 'Hot';
}
